export const EMPTY_ARRAY = [];
export const isAmplify = window.location.hostname.includes("amplifyapp");
export const isLocalhost = window.location.hostname === "localhost";
export const isDevEnv = window.location.hostname === "app.dev.seemplicity.io";

export const ACCOUNT_TYPES = {
  CUSTOMER: "CUSTOMER",
  PROSPECT: "PROSPECT",
  DEMO: "DEMO",
  RND_TESTING: "RND_TESTING",
  RND_ENRICHMENT: "RND_ENRICHMENT",
  CHANNEL_PARTNER: "CHANNEL_PARTNER",
  TECH_PARTNER: "TECH_PARTNER",
} as const;

export type AccountType = (typeof ACCOUNT_TYPES)[keyof typeof ACCOUNT_TYPES];

export const safeJsonParse = <T>(
  json: string | null | undefined,
  fallback: T
): T => {
  if (!json) {
    return fallback;
  }
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
};

/**
 * Encodes a string to URL-safe Base64
 */
export const encodeBase64Url = (input: string): string =>
  btoa(input).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * Decodes a URL-safe Base64 string back to original
 */
export const decodeBase64Url = (input: string): string => {
  let base64 = input.replace(/-/g, "+").replace(/_/g, "/");

  // restore padding
  const padding = base64.length % 4;
  if (padding) {
    base64 += "=".repeat(4 - padding);
  }

  return atob(base64);
};

const SAFE_URL_PROTOCOLS = new Set(["http:", "https:"]);

// Credentials in a link can disguise its real host (https://trusted.example@evil.example).
const isHttpUrl = (url: URL): boolean =>
  SAFE_URL_PROTOCOLS.has(url.protocol) && !url.username && !url.password;

export const parseHttpUrl = (value: string): URL | null => {
  try {
    const url = new URL(value);
    return isHttpUrl(url) ? url : null;
  } catch {
    return null;
  }
};

export const toSafeUrl = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    return isHttpUrl(url) ? url.href : null;
  } catch {
    return parseHttpUrl(`https://${value}`)?.href ?? null;
  }
};

export const pluralize = (
  count: number,
  singular: string,
  plural = `${singular}s`
): string => `${count} ${count === 1 ? singular : plural}`;
